import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { loginMiningAPI, storeMiningAccessToken, storeMiningUserData } from '../../api/miningApi';
import { useMiningAuth } from '../../context/MiningAuthContext';
import AppSwitcher from '../../components/AppSwitcher';

export default function MiningLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { loginMining } = useMiningAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await loginMiningAPI({ email, password });
      if (response.success) {
        storeMiningAccessToken(response.access_token);
        storeMiningUserData(response.user);
        loginMining(response.access_token, response.user);
        navigate('/mining/dashboard');
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (err) {
      setError('Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="absolute top-6 right-6 z-50">
        <AppSwitcher />
      </div>

      <div className="w-full max-w-sm">
        {/* Logo + title */}
        <div className="mb-8 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-ginox-green/30 bg-ginox-green/10 mb-5">
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="Ginox"
              className="h-8 w-8 object-contain"
            />
          </div>
          <div className="flex items-center justify-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-5 bg-ginox-green" aria-hidden="true" />
            Mining
            <span className="h-px w-5 bg-ginox-green" aria-hidden="true" />
          </div>
          <h1 className="font-serif italic text-[32px] leading-[1.05] text-foreground">
            Mining <span className="text-gradient-ginox">Admin</span>
          </h1>
          <p className="mt-1 text-xs text-muted-foreground font-mono tracking-[0.06em]">
            Sign in to your account
          </p>
        </div>

        {/* Form card */}
        <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-md bg-ginox-green px-4 py-2.5 text-sm font-semibold text-black hover:bg-ginox-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
